import React, { useState, useCallback, useMemo } from 'react';
import { Dropdown, Menu, Button, Divider } from '@arco-design/web-react';
import { IconDown } from '@arco-design/web-react/icon';
import { useBlock, useEditorProps, useFocusIdx, IconFont } from '@bay-email/editor';
import { getContextMergeTags } from '@extensions/utils/getContextMergeTags';
import { matchesMergeTagQuery } from '@extensions/utils/matchesMergeTagQuery';
import {
	getMergeTagsForContext,
	type MergeTagContext,
} from '@extensions/utils/getMergeTagsForContext';
import {
	t,
	dedupeMergeTagKeys,
	getMergeTagGroup,
	getMergeTagGroupLabel,
	getMergeTagGroupSortWeight,
	getMergeTagLabel,
	hasMergeTagKey,
	getAncestorCustomListingIdx,
	getValueByIdx,
	isListingBlockType,
} from '@bay-email/core';
import BayTooltip from '@bay-ui/BayTooltip';
import BaySearch from '@bay-ui/BaySearch';

function formatFieldLabel(fieldKey: string): string {
	return fieldKey
		.replace(/[_-]+/g, ' ')
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.trim()
		.replace(/\s+/g, ' ')
		.replace(/\b\w/g, char => char.toUpperCase());
}

export const MergeTags: React.FC<{
	onChange: (v: string) => void;
	value: string;
	isSelect?: boolean;
	iconOnly?: boolean;
	iconSize?: number | string;
	context?: MergeTagContext; // NEW: Filter merge tags by context
	icon?: React.ReactNode; // Allow custom icon
	className?: string; // Allow custom className for styling
	buttonType?: 'primary' | 'outline' | 'text'; // Allow custom button type
	tooltipPlacement?: string; // Allow custom tooltip placement
	children?: React.ReactElement; // Allow children to be passed in
	showSearch?: boolean; // Option to show search bar
}> = React.memo(props => {
	const { focusIdx } = useFocusIdx();
	const { mergeTags = {}, mergeTagGenerate } = useEditorProps();
	const { values, focusBlock } = useBlock();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const normalizedSearch = searchQuery.trim().toLowerCase();

	// Listing fields only exist for blocks that actually have somewhere to get a
	// listing FROM: inside a Custom Listing Wrapper, or inside a Listing
	// section's column. Anywhere else they are removed from the dropdown
	// entirely — not disabled — so the only way to design with listing data is
	// to use one of those two containers.
	// A listing block binds its own listing(s), so it is a listing scope too - without
	// this its Link Address menu offered no Listing URL (monday 13122054004).
	const hasListingScope = useMemo(() => {
		const content = (values as any)?.content;
		const focusedType = focusIdx ? getValueByIdx(values as any, focusIdx)?.type : undefined;
		return (
			Boolean(getAncestorCustomListingIdx(content, focusIdx)) ||
			isListingBlockType(focusedType)
		);
	}, [values, focusIdx]);

	const withoutListingTags = useCallback(
		(tags: Record<string, any>) =>
			Object.fromEntries(
				Object.entries(tags || {}).filter(
					([key]) => key !== 'listing' && getMergeTagGroup(key) !== 'listing',
				),
			),
		[],
	);

	const contextMergeTags = useMemo(() => {
		const tags = getContextMergeTags(mergeTags, values, focusIdx);
		// Apply context filter; defaults to 'no-image-src' when no context prop is passed
		const forContext = getMergeTagsForContext(tags, props.context ?? 'no-image-src');
		return hasListingScope ? forContext : withoutListingTags(forContext);
	}, [mergeTags, values, focusIdx, props.context, hasListingScope, withoutListingTags]);

	const menuData = useMemo(() => {
		const normalizedContextMergeTags: Record<string, Record<string, any>> = {};

		Object.entries(contextMergeTags || {}).forEach(([groupKey, groupData]) => {
			if (groupData && typeof groupData === 'object') {
				normalizedContextMergeTags[groupKey] = groupData as Record<string, any>;
				return;
			}

			if (typeof groupData === 'string' || typeof groupData === 'number') {
				const resolvedGroupKey = getMergeTagGroup(groupKey);
				if (!normalizedContextMergeTags[resolvedGroupKey]) {
					normalizedContextMergeTags[resolvedGroupKey] = {};
				}
				normalizedContextMergeTags[resolvedGroupKey][groupKey] = groupData;
			}
		});

		const resolveMergeTagKey = (groupKey: string, fieldKey: string): string => {
			const dottedKey = `${groupKey}.${fieldKey}`;
			if (hasMergeTagKey(dottedKey)) return dottedKey;
			if (hasMergeTagKey(fieldKey)) return fieldKey;
			return dottedKey;
		};

		const getFieldLabel = (groupKey: string, fieldKey: string): string => {
			const fullKey = resolveMergeTagKey(groupKey, fieldKey);
			return getMergeTagLabel(fullKey) || formatFieldLabel(fieldKey);
		};

		return {
			normalizedContextMergeTags,
			resolveMergeTagKey,
			getFieldLabel,
		};
	}, [contextMergeTags]);

	const onMenuClick = useCallback(
		(key: string) => {
			if (!key || !hasMergeTagKey(key)) return;

			return props.onChange(mergeTagGenerate(key));
		},
		[props, mergeTagGenerate],
	);

	// Always use our direct dropdown implementation - no more renderMergeTagContent override

	const orderedGroupKeys = Object.keys(menuData.normalizedContextMergeTags).sort(
		(a, b) => {
			const weightDiff =
				getMergeTagGroupSortWeight(a) - getMergeTagGroupSortWeight(b);
			if (weightDiff !== 0) return weightDiff;
			return a.localeCompare(b);
		},
	);

	// Create the grouped menu
	const dropList = (
		<Menu
			onClickMenuItem={onMenuClick}
			style={{
				maxHeight: 'unset',
				minWidth: 300,
				zIndex: 10000,
			}}
			className='p-0 border-0 shadow'
		>
			{props.showSearch && (
				<>
					<div
						className='p-4 rounded-top-1 bg-white'
						onMouseDown={e => e.stopPropagation()}
					>
						<BaySearch
							hideMenu
							searchIcon
							placeholder={t('Search')}
							searchInputValue={searchQuery}
							handleSearchInput={(val: string) => setSearchQuery(val ?? '')}
						/>
					</div>
					<Divider className='m-0' />
				</>
			)}
			{Object.keys(menuData.normalizedContextMergeTags).length === 0 ? (
				<Menu.Item
					disabled
					key='no-data'
				>
					{t('No merge tags available')}
				</Menu.Item>
			) : (
				<div
					className='overflow-auto pt-3 pb-2'
					style={{ maxHeight: props.showSearch ? 431 : 533 }}
				>
					{orderedGroupKeys.map((groupKey, groupIndex) => {
						const groupData = menuData.normalizedContextMergeTags[groupKey];
						const groupDisplayName = getMergeTagGroupLabel(groupKey);
						if (!groupData || typeof groupData !== 'object') return null;

						const dedupedFieldKeys = dedupeMergeTagKeys(
							Object.keys(groupData).map(fieldKey =>
								menuData.resolveMergeTagKey(groupKey, fieldKey),
							),
						).map(fullKey =>
							fullKey.includes('.')
								? fullKey.split('.').slice(1).join('.')
								: fullKey,
						);

						const menuItems = dedupedFieldKeys
							.filter(fieldKey => {
								const fieldValue = groupData[fieldKey];
								const fullKey = menuData.resolveMergeTagKey(groupKey, fieldKey);
								const isValidValue =
									typeof fieldValue === 'string' || typeof fieldValue === 'number';
								if (!isValidValue || !hasMergeTagKey(fullKey)) return false;
								if (!normalizedSearch) return true;
								const label = menuData.getFieldLabel(groupKey, fieldKey) ?? fieldKey;
								return matchesMergeTagQuery(
									label,
									fieldKey,
									fullKey,
									normalizedSearch,
								);
							})
							// As per the requirements, we need to show the fields alphabetically within each group.
							.sort((a, b) =>
								menuData
									.getFieldLabel(groupKey, a)
									.localeCompare(menuData.getFieldLabel(groupKey, b), undefined, {
										sensitivity: 'base',
										numeric: true,
									}),
							)
							.map(fieldKey => {
								const fieldDisplayName = menuData.getFieldLabel(groupKey, fieldKey);
								const mergeTagKey = menuData.resolveMergeTagKey(
									groupKey,
									fieldKey,
								);
								return (
									<Menu.Item key={mergeTagKey}>
										{fieldDisplayName}
									</Menu.Item>
								);
							});

						if (menuItems.length === 0) return null;

						return (
							<div
								key={groupKey}
								className='px-2 mx-1'
							>
								{groupIndex > 0 && <Divider className='my-3' />}
								<Menu.ItemGroup
									title={
										<div className='text-500 fw-bold text-uppercase mb-1'>
											{groupDisplayName}
										</div>
									}
								>
									{menuItems}
								</Menu.ItemGroup>
							</div>
						);
					})}
				</div>
			)}
		</Menu>
	);

	const handleDropdownVisibleChange = (visible: boolean) => {
		setIsDropdownOpen(visible);
		if (!visible) {
			setSearchQuery('');
		}
	};

	return (
		<div style={{ color: '#333' }}>
			<BayTooltip
				disabled={!props.iconOnly}
				overlay={<div>{t('Merge Tags')}</div>}
				placement={props.tooltipPlacement || 'top'}
			>
				<Dropdown
					droplist={dropList}
					trigger='click'
					position='br'
					getPopupContainer={() => document.body}
					triggerProps={{
						autoFitPosition: true,
						popupStyle: { zIndex: 99999 },
						onVisibleChange: handleDropdownVisibleChange,
					}}
				>
					{props.iconOnly ? (
						<Button
							type={props.buttonType || 'text'}
							size='small'
							icon={
								props.icon ? (
									props.icon
								) : (
									<IconFont
										size={typeof props.iconSize === 'number' ? props.iconSize : 16}
										iconName='icon-merge-tags'
									/>
								)
							}
							className={props.className}
							style={{
								background: isDropdownOpen ? 'var(--bs-primary)' : undefined,
								color: isDropdownOpen ? 'var(--bs-white)' : undefined,
							}}
							onMouseEnter={e => {
								if (!isDropdownOpen)
									e.currentTarget.style.background = 'var(--bs-blue-100)';
							}}
							onMouseLeave={e => {
								if (!isDropdownOpen) e.currentTarget.style.background = '';
							}}
						/>
					) : props.children ? (
						props.children
					) : (
						<Button
							size='small'
							type={props.buttonType || 'outline'}
							style={{ width: '100%', justifyContent: 'space-between' }}
						>
							<span style={{ flex: 1, textAlign: 'left' }}>
								{props.value || t('Select merge tag')}
							</span>
							<IconDown />
						</Button>
					)}
				</Dropdown>
			</BayTooltip>
		</div>
	);
});
